/**
 * A página de quem está logado: o que é meu, o que me avisaram, e quem eu sou.
 *
 * <p>As três abas respondem perguntas diferentes e por isso não viram uma tela só:
 * "o que eu faço agora", "o que aconteceu", "como eu apareço". A primeira é a que abre,
 * porque é a que se consulta todo dia.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../../api/cliente';
import type { Roadmap, Tarefa } from '../../api/tipos';
import { Avatar } from '../../comum/Avatar';
import { useSessao } from '../../auth/SessaoContext';
import { CHAVE_QUADRO } from '../devboard/tipos';
import type { Card, Coluna, Quadro } from '../devboard/tipos';
import { CHAVE_ROADMAP } from '../roadmapApi';
import {
  CHAVE_CONTAGEM,
  CHAVE_NOTIFICACOES,
  fraseDe,
  rotaDaNotificacao,
} from '../../shell/Notificacoes';
import type { Notificacao } from '../../shell/Notificacoes';
import { agrupaPorFaixa } from './agrupaNotificacoes';
import { PESO, prazoDaFrente } from './prazoDaFrente';
import type { Prazo } from './prazoDaFrente';
import './eu.css';

type Aba = 'tarefas' | 'notificacoes' | 'config';

/** As cores que o time já usa nos avatares — as mesmas do seed. */
const CORES = [
  '#E0A93A', '#86C33C', '#4A93E6', '#E0556E',
  '#A87BD0', '#3FD0A0', '#C9A25A', '#B04A45',
  '#4E9C6B', '#5B8FB9', '#C0703C', '#8E7CC3',
];

const dataHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

export function MinhaPagina() {
  const [busca, setBusca] = useSearchParams();
  const aba = (busca.get('aba') as Aba) ?? 'tarefas';

  function irPara(nova: Aba) {
    setBusca({ aba: nova }, { replace: true });
  }

  return (
    <div className="eu">
      <div className="eu-abas" role="tablist">
        {([
          ['tarefas', 'Minhas tasks'],
          ['notificacoes', 'Notificações'],
          ['config', 'Configurações'],
        ] as [Aba, string][]).map(([id, rotulo]) => (
          <button
            key={id}
            role="tab"
            aria-selected={aba === id}
            className={`eu-aba${aba === id ? ' on' : ''}`}
            onClick={() => irPara(id)}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {aba === 'tarefas' && <MinhasTarefas />}
      {aba === 'notificacoes' && <MinhasNotificacoes />}
      {aba === 'config' && <MinhasConfiguracoes />}
    </div>
  );
}

// ------------------------------------------------------------------ tarefas

/**
 * Uma linha da lista "o que é meu".
 *
 * <p>São dois formatos porque são duas coisas: a frente tem bloco e janela de meses, o
 * card tem coluna. Espremer os dois num tipo só exigiria campos opcionais que só metade
 * das linhas preenche — e é assim que uma tela começa a mostrar "undefined".
 */
type LinhaDaLista =
  | { tipo: 'frente'; tarefa: Tarefa; prazo: Prazo; rotulo: string; mesesRestantes: number | null }
  | { tipo: 'card'; card: Card; coluna: Coluna; prazo: Prazo; rotulo: string; mesesRestantes: null };

function MinhasTarefas() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [filtro, setFiltro] = useState<Prazo | 'todas' | 'abertas'>('abertas');

  /**
   * O roadmap inteiro, pela mesma chave da timeline.
   *
   * <p>Não há endpoint de "minhas tarefas": as 57 frentes já vêm com os responsáveis, e
   * filtrar aqui custa nada. Um endpoint novo seria uma segunda definição de "minha",
   * que é exatamente o tipo de coisa que diverge — e o cache é compartilhado, então
   * quem veio do Roadmap não paga nem a consulta.
   */
  const { data, isPending, error } = useQuery<Roadmap>({
    queryKey: CHAVE_ROADMAP,
    queryFn: () => api('/api/projetos/mistik-island/roadmap'),
  });

  /**
   * O quadro, pelo mesmo motivo — e porque card do Dev Board também é tarefa sua.
   *
   * <p>Faltava. Esta aba lia só o roadmap, então um card atribuído a você não aparecia
   * em lugar nenhum que respondesse "o que é meu": a notificação chegava e a lista
   * continuava sem ele. É a mesma raiz do aviso que não abria o card — uma coisa virou
   * duas, e as telas que perguntam "quais são as minhas" só sabiam de uma.
   *
   * <p>Sem {@code isPending} no caminho do erro: o quadro é acessório aqui, e uma falha
   * nele não pode esconder as frentes que já vieram.
   */
  const { data: quadro } = useQuery<Quadro>({
    queryKey: CHAVE_QUADRO,
    queryFn: () => api(`/api/projetos/mistik-island/quadro`),
  });

  if (isPending) return <div className="rm-estado">carregando…</div>;
  if (error) return <div className="rm-estado">não consegui carregar: {error.message}</div>;
  if (!usuario) return null;

  const hoje = new Date();
  const anoMes = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  const mesAtual = data.meses.find((m) => m.anoMes === anoMes)?.indice ?? null;
  const grupos = new Map(data.grupos.map((g) => [g.id, g]));

  /**
   * As duas origens numa lista só.
   *
   * <p>Uma linha da lista é sempre "algo que é meu": ou uma frente do Roadmap, com
   * janela de meses e bloco, ou um card do Dev Board, com coluna. O que muda é a
   * pastilha e para onde o clique leva.
   */
  const doRoadmap: LinhaDaLista[] = data.tarefas
    .filter((t) => t.responsaveis.includes(usuario.id))
    .map((t) => ({ tipo: 'frente' as const, tarefa: t, ...prazoDaFrente(t, mesAtual) }));

  const doQuadro: LinhaDaLista[] = (quadro?.colunas ?? []).flatMap((coluna) =>
    coluna.cards
      .filter((c) => c.responsaveis.includes(usuario.id))
      .map((card) => ({
        tipo: 'card' as const,
        card,
        coluna,
        // Concluído é concluído nos dois mundos. O resto do card não tem prazo — tem
        // lugar —, e a pastilha diz o nome da coluna em vez de inventar uma data.
        prazo: (card.concluida ? 'concluida' : 'sem-prazo') as Prazo,
        rotulo: card.concluida ? 'Concluído' : coluna.nome,
        mesesRestantes: null,
      }))
  );

  const minhas = [...doRoadmap, ...doQuadro].sort(
    (a, b) =>
      PESO[a.prazo] - PESO[b.prazo] ||
      // Dentro do mesmo peso: as frentes pelo mês de fim, os cards pela ordem das
      // colunas — que é o fluxo que o time desenhou.
      (a.tipo === 'frente' && b.tipo === 'frente'
        ? a.tarefa.mesFim - b.tarefa.mesFim
        : a.tipo === 'card' && b.tipo === 'card'
          ? a.coluna.ordem - b.coluna.ordem
          : 0)
  );

  const porPrazo = (p: Prazo) => minhas.filter((m) => m.prazo === p).length;

  const visiveis = minhas.filter((m) => {
    if (filtro === 'todas') return true;
    // "Abertas" é o padrão: o que se consulta é o que ainda dá trabalho. As concluídas
    // continuam a um clique, sem empurrar as vivas para baixo da dobra.
    if (filtro === 'abertas') return m.prazo !== 'concluida';
    return m.prazo === filtro;
  });

  const chips: [typeof filtro, string, number][] = [
    ['abertas', 'Abertas', minhas.filter((m) => m.prazo !== 'concluida').length],
    ['atrasada', 'Atrasadas', porPrazo('atrasada')],
    ['vencendo', 'Vencem este mês', porPrazo('vencendo')],
    ['em-andamento', 'Em andamento', porPrazo('em-andamento')],
    ['a-comecar', 'A começar', porPrazo('a-comecar')],
    ['sem-prazo', 'Dev Board', porPrazo('sem-prazo')],
    ['concluida', 'Concluídas', porPrazo('concluida')],
    ['todas', 'Todas', minhas.length],
  ];

  const concluidas = porPrazo('concluida');
  const pct = minhas.length > 0 ? Math.round((concluidas / minhas.length) * 100) : 0;
  const vencendo = porPrazo('vencendo');
  const atrasadas = porPrazo('atrasada');

  return (
    <div>
      {/*
        O placar do que é meu.

        A aba respondia "o que eu faço agora" só pela lista; faltava a outra metade da
        pergunta — "como estou indo". O número que importa é a fração concluída, e ele é
        de leitura instantânea numa barra, não contando linhas.

        A frase da direita é o que aperta: atrasadas primeiro, depois o que vence este
        mês. Ela some quando não há nem uma nem outra, porque aí não há o que apressar —
        e um aviso que aparece sempre deixa de ser aviso.
      */}
      {minhas.length > 0 && (
        <div className="eu-placar">
          <div className="eu-placar-n">
            <b>{concluidas}</b>
            <span>
              de {minhas.length} concluída{minhas.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="eu-placar-trilho">
            <div className="eu-placar-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="eu-placar-pct">{pct}%</span>
          {(atrasadas > 0 || vencendo > 0) && (
            <span className={`eu-placar-aviso${atrasadas > 0 ? ' urgente' : ''}`}>
              {atrasadas > 0
                ? `${atrasadas} atrasada${atrasadas === 1 ? '' : 's'}`
                : `${vencendo} vence${vencendo === 1 ? '' : 'm'} este mês`}
            </span>
          )}
        </div>
      )}

      <div className="eu-filtros">
        {chips.map(([id, rotulo, quantas]) => (
          <button
            key={id}
            // A cor do chip é a do estado que ele filtra — a mesma do selo no card. Com
            // todos neutros, escolher um filtro era ler oito rótulos; com a cor, o olho
            // vai direto no que procura.
            className={`eu-chip pz-${id}${filtro === id ? ' on' : ''}${
              quantas === 0 ? ' vazio' : ''
            }`}
            onClick={() => setFiltro(id)}
            aria-pressed={filtro === id}
          >
            {rotulo}
            <span className="n">{quantas}</span>
          </button>
        ))}
      </div>

      {minhas.length === 0 ? (
        <div className="eu-vazio">
          <b>Nada atribuído a você</b>
          Quando alguém puser seu nome numa frente do Roadmap ou num card do Dev Board,
          ela aparece aqui — e você recebe uma notificação.
        </div>
      ) : visiveis.length === 0 ? (
        <div className="eu-vazio">
          <b>Nada neste filtro</b>
          Experimente "Todas".
        </div>
      ) : (
        <div className="eu-lista">
          {visiveis.map((linha) => {
            // Nome, checklist e comentários são iguais nos dois; o resto não é.
            const alvo = linha.tipo === 'frente' ? linha.tarefa : linha.card;
            const { prazo, rotulo } = linha;

            return (
              <button
                key={`${linha.tipo}-${alvo.id}`}
                // `pz-*` traz a cor do estado; a classe solta continua respondendo pelo
                // que é layout (a atrasada com borda, a concluída apagada).
                className={`eu-card pz-${prazo} ${prazo}`}
                onClick={() =>
                  navegar(
                    linha.tipo === 'frente'
                      ? `/roadmap?frente=${alvo.id}`
                      : `/dev-board?card=${alvo.id}`
                  )
                }
              >
                <span
                  className="eu-bloco"
                  style={{
                    background:
                      linha.tipo === 'frente'
                        ? grupos.get(linha.tarefa.grupoId)?.cor
                        : (linha.coluna.cor ?? 'var(--gold)'),
                  }}
                />
                <span className="eu-card-corpo">
                  <span className="eu-card-topo">
                    <b>{alvo.nome}</b>
                    <span className={`eu-selo ${prazo}`}>{rotulo}</span>
                  </span>
                  <span className="eu-card-meta">
                    {linha.tipo === 'frente' ? (
                      <>
                        {grupos.get(linha.tarefa.grupoId)?.nome}
                        <span className="eu-sep">·</span>
                        {janelaEmTexto(linha.tarefa, data.meses)}
                        {linha.mesesRestantes != null && prazo !== 'concluida' && (
                          <>
                            <span className="eu-sep">·</span>
                            {textoDoRestante(linha.mesesRestantes)}
                          </>
                        )}
                      </>
                    ) : (
                      // O card não tem janela para mostrar: o que localiza é o quadro.
                      <>Dev Board</>
                    )}
                    {alvo.checklist.total > 0 && (
                      <>
                        <span className="eu-sep">·</span>
                        {alvo.checklist.feitos}/{alvo.checklist.total} no checklist
                      </>
                    )}
                    {alvo.comentarios > 0 && (
                      <>
                        <span className="eu-sep">·</span>
                        {alvo.comentarios} comentário{alvo.comentarios > 1 ? 's' : ''}
                      </>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function janelaEmTexto(t: Tarefa, meses: Roadmap['meses']): string {
  const de = meses.find((m) => m.indice === t.mesInicio);
  const ate = meses.find((m) => m.indice === t.mesFim);
  if (!de || !ate) return '';
  const curto = (m: typeof de) => `${m.rotulo}/${String(m.ano).slice(2)}`;
  return de.indice === ate.indice ? curto(de) : `${curto(de)} → ${curto(ate)}`;
}

function textoDoRestante(meses: number): string {
  if (meses < 0) return `${-meses} ${-meses === 1 ? 'mês' : 'meses'} de atraso`;
  if (meses === 0) return 'termina este mês';
  return `faltam ${meses} ${meses === 1 ? 'mês' : 'meses'}`;
}

// ------------------------------------------------------------- notificações

/**
 * O que cada tipo de aviso é, de relance.
 *
 * <p>O ícone e a cor vêm dos quatro gatilhos definidos pelo Theo, e a ordem de
 * importância é a mesma que o `NotificacaoService` usa para desempatar: a <b>menção</b>
 * ganha do respingo, porque foi um chamado direto. É por isso que ela é a única em
 * dourado — as outras três são informação, ela é convocação.
 */
const MARCA_DA_NOTIFICACAO: Record<
  Notificacao['tipo'],
  { icone: string; classe: string; rotulo: string }
> = {
  MENCAO: { icone: '@', classe: 'mencao', rotulo: 'Citaram você' },
  TAREFA_ATRIBUIDA: { icone: '◆', classe: 'atribuida', rotulo: 'Você é responsável' },
  SUBTAREFA_ATRIBUIDA: { icone: '☑', classe: 'subtarefa', rotulo: 'Sub-tarefa sua' },
  COMENTARIO_NA_TAREFA: { icone: '✎', classe: 'comentario', rotulo: 'Comentaram' },
};

function MinhasNotificacoes() {
  const navegar = useNavigate();
  const fila = useQueryClient();
  const { usuario } = useSessao();
  const [so, setSo] = useState<'todas' | 'nao-lidas'>('todas');

  const { data, isPending } = useQuery<Notificacao[]>({
    queryKey: [...CHAVE_NOTIFICACOES, 'todas'],
    queryFn: () => api('/api/notificacoes?limite=200'),
  });

  const recarregar = () => {
    void fila.invalidateQueries({ queryKey: CHAVE_NOTIFICACOES });
    void fila.invalidateQueries({ queryKey: CHAVE_CONTAGEM });
  };

  const marcarUma = useMutation({
    mutationFn: (id: number) => api<void>(`/api/notificacoes/${id}/lida`, { method: 'POST' }),
    onSuccess: recarregar,
  });
  const marcarTodas = useMutation({
    mutationFn: () => api<void>('/api/notificacoes/lidas', { method: 'POST' }),
    onSuccess: recarregar,
  });

  if (isPending) return <div className="rm-estado">carregando…</div>;

  const lista = data ?? [];
  const naoLidas = lista.filter((n) => !n.lida).length;
  const visiveis = so === 'nao-lidas' ? lista.filter((n) => !n.lida) : lista;

  return (
    <div>
      <div className="eu-barra">
        {/*
          Dois recortes, e não um filtro por tipo: a pergunta que a tela responde é "o que
          eu ainda não vi". Filtrar por menção ou por atribuição seria oferecer um corte
          que ninguém pediu numa lista que já cabe na rolagem.
        */}
        <div className="eu-notif-abas" role="group" aria-label="Filtrar notificações">
          <button
            className={`eu-chip${so === 'todas' ? ' on' : ''}`}
            onClick={() => setSo('todas')}
            aria-pressed={so === 'todas'}
          >
            Todas<span className="n">{lista.length}</span>
          </button>
          <button
            className={`eu-chip${so === 'nao-lidas' ? ' on' : ''}${naoLidas === 0 ? ' vazio' : ''}`}
            onClick={() => setSo('nao-lidas')}
            aria-pressed={so === 'nao-lidas'}
          >
            Não lidas<span className="n">{naoLidas}</span>
          </button>
        </div>
        {naoLidas > 0 && (
          <button className="eu-acao" onClick={() => marcarTodas.mutate()}>
            marcar todas como lidas
          </button>
        )}
      </div>

      {lista.length === 0 ? (
        <div className="eu-vazio">
          <b>Nada por aqui</b>
          Você é avisado quando recebe uma frente ou uma sub-tarefa, é citado num
          comentário, ou alguém comenta numa frente sua.
        </div>
      ) : visiveis.length === 0 ? (
        <div className="eu-vazio">
          <b>Nada não lido</b>
          Você está em dia. Veja "todas" para o histórico.
        </div>
      ) : (
        // Partida por dia: numa parede de até 200 avisos, "o que aconteceu desde ontem"
        // — que é a pergunta que traz alguém aqui — só se responde comparando data por
        // data. Ver `agrupaNotificacoes`.
        agrupaPorFaixa(visiveis, (n) => n.criadoEm).map((grupo) => (
          <div key={grupo.faixa} className="eu-faixa">
            <h3 className="eu-faixa-tit">
              {grupo.rotulo}
              <span className="eu-faixa-n">{grupo.itens.length}</span>
            </h3>
            <div className="eu-lista">
              {grupo.itens.map((n) => {
                const { acao, alvo } = fraseDe(n, usuario?.id);
                const marca = MARCA_DA_NOTIFICACAO[n.tipo];
                return (
                  <button
                    key={n.id}
                    className={`eu-notif nt-${marca.classe}${n.lida ? '' : ' nova'}`}
                    onClick={() => {
                      if (!n.lida) marcarUma.mutate(n.id);
                      navegar(rotaDaNotificacao(n));
                    }}
                  >
                    <span className="eu-notif-av">
                      <Avatar
                        className="eu-av"
                        nome={n.autorNome ?? '—'}
                        cor={n.autorCor}
                        url={n.autorAvatar}
                      />
                      {/*
                        O selo do tipo, no canto do avatar. É ele que diz de relance se o
                        aviso é um chamado direto (@) ou um respingo — a frase abaixo diz
                        a mesma coisa, mas exige lê-la, e numa lista longa ninguém lê
                        todas antes de escolher.
                      */}
                      <span className="eu-notif-tipo" title={marca.rotulo} aria-hidden="true">
                        {marca.icone}
                      </span>
                    </span>
                    <span className="eu-notif-corpo">
                      <span className="eu-notif-frase">
                        {acao} <b>{alvo}</b>
                      </span>
                      {n.trecho && <span className="eu-notif-trecho">“{n.trecho}”</span>}
                      <span className="eu-notif-quando">{dataHora(n.criadoEm)}</span>
                    </span>
                    {!n.lida && <span className="eu-ponto" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ------------------------------------------------------------ configurações

function MinhasConfiguracoes() {
  const { usuario, definirUsuario } = useSessao();
  const [nome, setNome] = useState(usuario?.nome ?? '');
  const [cor, setCor] = useState(usuario?.cor ?? CORES[0]);
  const [cargo, setCargo] = useState(usuario?.cargo ?? '');
  const [email, setEmail] = useState(usuario?.email ?? '');
  const [avatar, setAvatar] = useState(usuario?.avatar ?? '');
  const [subindo, setSubindo] = useState(false);
  const [erroDaFoto, setErroDaFoto] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);
  const campoDeArquivo = useRef<HTMLInputElement>(null);

  /**
   * A foto sobe na hora de escolher, e só a url entra no formulário.
   *
   * <p>É o mesmo `/api/arquivos` das imagens do mundo — ele decodifica a imagem para
   * validá-la, então um PDF renomeado para `.png` é recusado aqui e não no banco.
   *
   * <p>Subir agora e salvar depois deixa um arquivo órfão no disco se a pessoa desistir.
   * É o preço já pago no anexo da tarefa, e pela mesma razão: o contrário — segurar o
   * arquivo até o Salvar — é o que faz a pessoa não ver a própria foto antes de gravar.
   */
  async function escolherFoto(arquivo: File | undefined) {
    if (!arquivo) return;
    setErroDaFoto(null);
    setSubindo(true);
    try {
      const corpo = new FormData();
      corpo.append('arquivo', arquivo);
      const { url } = await api<{ url: string }>('/api/arquivos', {
        method: 'POST',
        body: corpo,
      });
      setAvatar(url);
    } catch (e) {
      setErroDaFoto(e instanceof Error ? e.message : 'Não deu para enviar a foto.');
    } finally {
      setSubindo(false);
      if (campoDeArquivo.current) campoDeArquivo.current.value = '';
    }
  }

  const salvar = useMutation({
    mutationFn: () =>
      api<NonNullable<typeof usuario>>('/api/eu', {
        method: 'PATCH',
        body: JSON.stringify({
          nome: nome.trim(),
          cor,
          cargo: cargo.trim(),
          email: email.trim(),
          avatar,
        }),
      }),
    onSuccess: (atualizado) => {
      // A sessão troca na hora: o avatar do topo e todo lugar que desenha iniciais leem
      // daqui, e recarregar a página para ver o próprio nome seria estranho.
      definirUsuario(atualizado);
      setSalvo(true);
      setTimeout(() => setSalvo(false), 2500);
    },
  });

  if (!usuario) return null;

  const mudou =
    nome.trim() !== usuario.nome ||
    cor !== usuario.cor ||
    cargo.trim() !== usuario.cargo ||
    email.trim() !== usuario.email ||
    avatar !== usuario.avatar;

  // Trocar o e-mail troca o login de quem entra por senha. Quem entra pelo Google não
  // sente nada, porque lá a identidade é a conta Google e não o endereço — mas daqui
  // não dá para saber qual é o caso, então o aviso vale para os dois.
  const trocouEmail = email.trim().toLowerCase() !== usuario.email.toLowerCase();

  return (
    <div className="eu-config">
      <div className="eu-previa">
        <Avatar
          className="eu-previa-av"
          nome={nome.trim() || usuario.nome}
          cor={cor}
          url={avatar || null}
        />
        <div>
          <b>{nome.trim() || usuario.nome}</b>
          <span>@{usuario.handle}</span>
        </div>

        <div className="eu-foto-acoes">
          <input
            ref={campoDeArquivo}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            hidden
            onChange={(e) => void escolherFoto(e.target.files?.[0])}
          />
          <button
            className="eu-btn fantasma"
            disabled={subindo}
            onClick={() => campoDeArquivo.current?.click()}
          >
            {subindo ? 'enviando…' : avatar ? 'Trocar foto' : 'Enviar foto'}
          </button>
          {avatar && (
            <button className="eu-btn fantasma" onClick={() => setAvatar('')}>
              Remover
            </button>
          )}
        </div>
      </div>

      {erroDaFoto && <div className="eu-erro">{erroDaFoto}</div>}

      <label className="eu-campo">
        <span>Nome</span>
        <input
          value={nome}
          maxLength={200}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Como você aparece para o time"
        />
      </label>

      <div className="eu-campo">
        <span>Cor do avatar</span>
        <div className="eu-cores">
          {CORES.map((c) => (
            <button
              key={c}
              className={`eu-cor${cor === c ? ' on' : ''}`}
              style={{ background: c }}
              onClick={() => setCor(c)}
              aria-label={`Cor ${c}`}
              aria-pressed={cor === c}
            />
          ))}
        </div>
      </div>

      <label className="eu-campo">
        <span>Cargo</span>
        <input
          value={cargo}
          maxLength={120}
          onChange={(e) => setCargo(e.target.value)}
          placeholder="O que você faz no time"
        />
      </label>

      <label className="eu-campo">
        <span>E-mail</span>
        <input
          type="email"
          value={email}
          maxLength={200}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@goyazgames.com"
        />
        {/*
          O aviso aparece só quando o campo mudou de verdade — um alerta permanente ao
          lado de um campo que ninguém tocou vira parte do cenário e some da vista.
        */}
        {trocouEmail && (
          <small className="eu-aviso">
            Este é o e-mail com que você entra. Depois de salvar, use o novo para fazer
            login com senha — quem entra pelo Google não é afetado.
          </small>
        )}
      </label>

      {/* Papel continua fora, e é o único que fica: papel é permissão, e mudar a própria
          permissão pelas configurações seria se promover sozinho. */}
      <div className="eu-fixos">
        <div>
          <span>Papel</span>
          <b>{usuario.papel === 'ADMIN' ? 'Admin' : 'Editor'}</b>
        </div>
        <div>
          <span>Handle</span>
          <b>@{usuario.handle}</b>
        </div>
      </div>

      <div className="eu-salvar">
        <button
          className="eu-btn"
          disabled={!mudou || !nome.trim() || salvar.isPending}
          onClick={() => salvar.mutate()}
        >
          {salvar.isPending ? 'salvando…' : 'Salvar'}
        </button>
        {salvo && <span className="eu-ok">salvo</span>}
        {salvar.error && <span className="eu-erro">{salvar.error.message}</span>}
      </div>
    </div>
  );
}
